/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import { css } from '@emotion/react';
import { UseEuiTheme, tintOrShade } from '../../services';
import { logicalCSS } from '../../global_styling';

export const euiCommentEventBorderColors = ({
  euiTheme,
  colorMode,
}: UseEuiTheme) => {
  const ratio = 0.6;
  return {
    warning: css`
      border-color: ${tintOrShade(euiTheme.colors.warning, 0.4, colorMode)};
    `,
    accent: css`
      border-color: ${tintOrShade(euiTheme.colors.accent, ratio, colorMode)};
    `,
    primary: css`
      border-color: ${tintOrShade(euiTheme.colors.primary, ratio, colorMode)};
    `,
    success: css`
      border-color: ${tintOrShade(euiTheme.colors.success, ratio, colorMode)};
    `,
    danger: css`
      border-color: ${tintOrShade(euiTheme.colors.danger, ratio, colorMode)};
    `,
    subdued: css`
      border-color: ${euiTheme.border.color};
    `,
    transparent: css`
      border-color: ${euiTheme.border.color};
    `,
    plain: css`
      border-color: ${euiTheme.border.color};
    `,
  };
};

export const euiCommentEventStyles = (euiThemeContext: UseEuiTheme) => {
  const { euiTheme } = euiThemeContext;
  return {
    euiCommentEvent: css`
      overflow: hidden;
    `,
    border: css`
      border-width: ${euiTheme.border.width.thin};
      border-style: solid;
      border-radius: ${euiTheme.border.radius.medium};
    `,
  };
};

export const euiCommentEventHeaderStyles = (euiThemeContext: UseEuiTheme) => {
  const { euiTheme } = euiThemeContext;

  return {
    euiCommentEvent__header: css``,
    border: css`
      ${logicalCSS('border-bottom-style', 'solid')}
      ${logicalCSS('border-bottom-width', euiTheme.border.width.thin)}
    `,
    // Children
    euiCommentEvent__headerMain: css`
      display: flex;
      flex: 1;
      gap: ${euiTheme.size.s};
    `,
    euiCommentEvent__headerData: css`
      display: flex;
      flex: 1;
      align-items: center;
      flex-wrap: wrap;
      gap: ${euiTheme.size.xs};
    `,
    euiCommentEvent__headerEventIcon: css`
      ${logicalCSS('margin-right', euiTheme.size.xs)}
    `,
    euiCommentEvent__headerUsername: css`
      font-weight: ${euiTheme.font.weight.semiBold};
    `,
    euiCommentEvent__headerEvent: css`
      align-items: center;
      display: inline-flex;
      /* the header event can have inline badges so we're adding some white-space and flex-wrap properties  */
      white-space: pre-wrap;
      flex-wrap: wrap;
    `,
    euiCommentEvent__headerActions: css`
      display: flex;
      flex-wrap: wrap;
      gap: ${euiTheme.size.xs};
    `,
  };
};

export const euiCommentEventBodyStyles = ({ euiTheme }: UseEuiTheme) => ({
  euiCommentEvent__body: css``,
  // types
  regular: css`
    padding: ${euiTheme.size.s};
  `,
  update: css`
    ${logicalCSS('padding-top', euiTheme.size.xs)}
  `,
  custom: css``,
});
